import { useCallback, useEffect, useState } from 'react';
import { can, faNum, formatPercent, formatRial, type ReconUnpriced } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { useApiResource } from '../lib/useApiResource';
import { useAuth } from '../auth/AuthProvider';
import {
  Button,
  Card,
  ErrorRetry,
  Field,
  Input,
  Kpi,
  Money,
  NumberInput,
  Tabs,
  useToast,
} from '../ui';
import { YieldVarianceCard } from './YieldVarianceCard';

interface CloseData {
  ym: string;
  releasedRevenue: number;
  realizedCost: number;
  actualGross: number;
  inflationErosion: number;
  grossMargin: number | null;
  // OWNERNET-GATE (Q-8 / SEC-06, second pass PREPROD-2): null for a caller without ownernet.read
  // (Admin+Finance). The whole RECONSTRUCTION SET is nulled together — the period inputs, plus the two
  // cards that carry `other_income` verbatim (scrapCover.actualIncome / coverAssessment.cover) — because
  // ownerNetRealized = actualGross − (conversionPool + ga + financial) + other_income and every addend was
  // on this wire. Only Admin holds `config.write`, so the save form these prefill is Admin-only anyway.
  inputs: Record<string, number> | null;
  opex: number | null;
  conversionPool: number;
  poolSource: string;
  absorbedConversion: number;
  absorptionVariance: number;
  ownerNetRealized: number | null;
  /** D6 scrap-cover doctrine: did the ~5%-of-sales scrap income (assumed 0 in pricing) materialize? */
  scrapCover: {
    assumedPct: number;
    assumedCover: number;
    actualIncome: number;
    actualPct: number | null;
    shortfall: number;
    ratioOfAssumed: number | null;
  } | null;
  coverAssessment: {
    cover: number;
    drawn: number;
    costInflation: number;
    underAbsorption: number;
    headroom: number;
    adequate: boolean;
  } | null;
  byProduct: Record<string, { qty: number; revenue: number; gross: number }>;
  byDealErosion: {
    dealId: number;
    dealNo: string;
    customer: string;
    qty: number;
    erosion: number;
  }[];
  /** period lock (E15-01): true once the month is closed → inputs frozen, reopen to amend */
  isLocked: boolean;
  /** optimistic-concurrency token (E16-21) — echoed on save; a stale value is rejected (409) */
  version: number;
  /** FLOW-4: cached reconciliation headline (readReconciliationCache) — null when the book was never
   *  reconciled (NO DATA, not "ok"). When diverged, locking the month requires an explicit ack. */
  bookHealth: { diverged: boolean; worstLabel: string | null; worstResidual: number } | null;
  /** BIDX-P4: book-wide receipts still owing a confirmed price. Informational — never blocks the close. */
  unpriced: ReconUnpriced;
}

// Phase 2: labour/overhead retired here — the conversion pool's ONE home is «فرض ماهانه» (Assumption
// Master, تنظیمات), the same number that prices motors. This screen keeps the true PERIOD inputs.
const INPUT_FIELDS: [key: string, label: string][] = [
  ['ga', 'هزینهٔ فروش و عمومی (G&A)'],
  ['financial', 'هزینهٔ مالی (تنزیل چک/بهره)'],
  ['other_income', 'سایر درآمد (ضایعات و…)'],
];

/** TKT-11 Tier-3b — the AI month-close pack: a one-click Persian memo bridging quoted→realized margin
 *  (cost inflation vs under-absorption) with actions, generated on demand from the month's close numbers. */
function AiCloseMemoCard({ ym }: { ym: string }) {
  const toast = useToast();
  const [memo, setMemo] = useState('');
  const [busy, setBusy] = useState(false);
  async function generate() {
    setBusy(true);
    const r = await api<{ memo?: string; reply?: string }>('/api/ai/close-memo', {
      method: 'POST',
      body: JSON.stringify({ ym }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    const b = r.body as { memo?: string; reply?: string };
    if (b.memo) setMemo(b.memo);
    else {
      setMemo('');
      toast(b.reply ?? 'گزارشی تولید نشد', 'error');
    }
  }
  return (
    <Card title="گزارش بستن ماه — هوش مصنوعی">
      <p className="muted" style={{ marginTop: 0 }}>
        خلاصهٔ عملکرد ماه و پلِ انتظار→واقعیت (تورمِ هزینه در برابر کم‌جذبیِ تبدیل) با پیشنهاد
        اقدام.
      </p>
      <Button onClick={() => void generate()} loading={busy} disabled={busy}>
        {busy ? 'در حال تهیه…' : `تهیهٔ گزارش هوش مصنوعی برای ${ym}`}
      </Button>
      {memo ? (
        <div style={{ marginTop: 12, whiteSpace: 'pre-wrap', lineHeight: 1.9 }}>{memo}</div>
      ) : null}
    </Card>
  );
}

export function CloseScreen() {
  const { user } = useAuth();
  const toast = useToast();
  const canEdit = user ? can(user.role, 'config.write') : false;
  // P4-REPAIR: this screen is Admin+Finance, but «ثبت قیمت» renders under purchase.write
  // (Admin/Editor/Purchasing) — so Finance, the canonical closer, cannot press the button the old copy
  // told them to. Name the action AND who performs it (prior art: data/repositories/workOrder.ts).
  const canPrice = user ? can(user.role, 'purchase.write') : false;
  const {
    data: monthsData,
    status: monthsStatus,
    error: monthsError,
    reload: reloadMonths,
  } = useApiResource<string[]>('/api/months');
  const months = monthsData ?? [];
  const [ym, setYm] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState<CloseData | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // FLOW-4: the operator's explicit acknowledgment of a live reconciliation divergence, required before the
  // lock. Reset on every (re)load so a stale tick can never carry into a different month or a fresh headline.
  const [ackResidual, setAckResidual] = useState(false);

  const load = useCallback(async (month: string) => {
    const r = await api<CloseData>(`/api/close?ym=${encodeURIComponent(month)}`);
    if (r.ok) {
      const d = r.body as CloseData;
      setData(d);
      setAckResidual(false);
      setDraft(
        Object.fromEntries(
          Object.entries(d?.inputs ?? {}).map(([k, v]) => [k, v ? String(v) : '']),
        ),
      );
    }
  }, []);

  useEffect(() => {
    if (!Array.isArray(monthsData)) return;
    const initial = monthsData[0] ?? new Date().toISOString().slice(0, 7);
    setYm(initial);
    void load(initial);
  }, [monthsData, load]);

  async function save() {
    setBusy(true);
    // Echo the version we loaded (E16-21) so a stale close is rejected instead of clobbering another closer.
    const payload: Record<string, unknown> = { ym, version: data?.version ?? 0 };
    // FLOW-4: carry the operator's explicit acknowledgment; the server refuses the lock over a live
    // divergence without it (audited "proceed knowingly"). Harmless when the book isn't diverged.
    if (ackResidual) payload.ack_reconciliation = true;
    for (const [k, v] of Object.entries(draft)) payload[k] = v === '' ? 0 : Number(v);
    const r = await api<CloseData>('/api/close', { method: 'POST', body: JSON.stringify(payload) });
    setBusy(false);
    if (!r.ok) {
      const body = r.body as { error?: string; message?: string; current?: CloseData };
      // 409 conflict: another closer saved first. Reload their fresh figures (never silently overwrite).
      if (body?.error === 'version_conflict' && body.current) {
        setData(body.current);
        setDraft(
          Object.fromEntries(
            Object.entries(body.current.inputs ?? {}).map(([k, v]) => [k, v ? String(v) : '']),
          ),
        );
      }
      return toast(body?.message ?? errMsg(r), 'error');
    }
    setData(r.body as CloseData);
    // Closing auto-locks the month (E15-01) — reflect it and tell the owner the P&L is now frozen.
    toast((r.body as CloseData).isLocked ? 'ماه بسته و قفل شد ✓' : 'ماه ذخیره شد');
  }

  // Reopen a locked month to amend it (E15-01): the deliberate, audited way back in before re-closing.
  async function reopen() {
    setBusy(true);
    const r = await api('/api/assumptions/month/lock', {
      method: 'POST',
      body: JSON.stringify({ ym, locked: false }),
    });
    setBusy(false);
    if (!r.ok) return toast((r.body as { message?: string }).message ?? 'خطا در بازگشایی', 'error');
    await load(ym);
    toast('ماه بازگشایی شد — اکنون قابل ویرایش است');
  }

  const products = data ? Object.entries(data?.byProduct ?? {}) : [];

  return (
    <>
      {monthsStatus === 'error' ? (
        <ErrorRetry error={monthsError} onRetry={() => reloadMonths()} />
      ) : monthsStatus === 'loading' ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : null}
      {/* Month scope — lifted above the tabs so it governs every tab's figures. */}
      <div className="row" style={{ marginBottom: 12 }}>
        <Field label="ماه (YYYY-MM)">
          <Input
            style={{ maxWidth: 160 }}
            value={ym}
            onChange={(e) => setYm(e.target.value)}
            onBlur={() => void load(ym)}
          />
        </Field>
        {months.length ? (
          <span className="muted">ماه‌های دارای خروج: {months.join('، ')}</span>
        ) : null}
      </div>
      <Tabs
        storageKey="close"
        items={[
          {
            id: 'ai',
            label: 'گزارش هوش مصنوعی',
            render: () => <AiCloseMemoCard ym={ym} />,
          },
          {
            id: 'close',
            label: 'بستن ماه',
            render: () => (
              <Card title="بستن ماه — برای هوش تجاری (نه حسابداری)">
                <p className="muted" style={{ marginTop: 0 }}>
                  فروش از «خروج انبار» محاسبه می‌شود؛ فقط چند عدد پایان‌ماه را وارد کنید. حساب رسمی
                  در سپیدار است.
                </p>
                {data ? (
                  <div style={{ marginTop: 8 }}>
                    <Kpi label="درآمد آزادشده" value={<Money value={data.releasedRevenue} />} />
                    <Kpi label="بهای واقعی مواد" value={<Money value={data.realizedCost} />} />
                    <Kpi
                      label="سود ناخالص واقعی"
                      value={<Money value={data.actualGross} />}
                      tone={data.actualGross >= 0 ? 'good' : 'bad'}
                    />
                    <Kpi label="حاشیه" value={formatPercent(data.grossMargin)} />
                    <Kpi
                      label="فرسایش تورمی (پیشنهاد→تحویل)"
                      value={<Money value={data.inflationErosion} />}
                      tone={data.inflationErosion > 0 ? 'bad' : 'default'}
                    />
                  </div>
                ) : null}
              </Card>
            ),
          },
          {
            id: 'expenses',
            label: 'هزینه‌ها',
            render: () => (
              <Card title="هزینه‌های پایان ماه (پارتو)">
                {data ? (
                  <p className="muted" style={{ marginTop: 0 }}>
                    هزینهٔ تبدیل ماه (دستمزد + سربار تولید):{' '}
                    <b>{formatRial(data.conversionPool)}</b>
                    {data.poolSource === 'legacy_close'
                      ? ' — از ثبت قدیمی همین صفحه (ماه‌های گذشته)'
                      : ' — از «فرض ماهانه» (تنظیمات)؛ همان عددی که موتورها را قیمت می‌گذارد'}
                    {' · '}جذب‌شده در فروش: <b>{formatRial(data.absorbedConversion)}</b>
                    {' · '}انحراف جذب:{' '}
                    <b
                      style={{ color: data.absorptionVariance > 0 ? 'var(--red)' : 'var(--green)' }}
                    >
                      {formatRial(data.absorptionVariance)}
                    </b>{' '}
                    (مثبت = جذب‌نشده)
                  </p>
                ) : null}
                <div className="form-grid">
                  {INPUT_FIELDS.map(([key, label]) => (
                    <Field key={key} label={label}>
                      <NumberInput
                        value={draft[key] ?? ''}
                        onValue={(n) => setDraft({ ...draft, [key]: n ? String(n) : '' })}
                        disabled={!canEdit || !!data?.isLocked}
                      />
                    </Field>
                  ))}
                </div>
                {data ? (
                  <div className="totalbox">
                    {/* OWNERNET-GATE: the owner's realized bottom line + its opex input are shown only to
                        ownernet.read roles (Admin+Finance); the server sends null to anyone else. */}
                    {data.ownerNetRealized != null && data.opex != null ? (
                      <>
                        جمع هزینه‌ها (تبدیل + G&A + مالی): <b>{formatRial(data.opex)}</b> ·{' '}
                        <b
                          style={{
                            color: data.ownerNetRealized < 0 ? 'var(--red)' : 'var(--green)',
                          }}
                        >
                          سود خالص مالک ماه: {formatRial(data.ownerNetRealized)}
                        </b>
                      </>
                    ) : null}
                    {data.scrapCover &&
                    data.scrapCover.assumedPct > 0 &&
                    data.releasedRevenue > 0 ? (
                      // D6: pricing assumes scrap income (~assumedPct of sales, booked 0) covers surprise costs —
                      // this line verifies the cover actually materialized. ≥1 covered · ≥½ thin · <½ drifting.
                      <div
                        style={{
                          color:
                            (data.scrapCover.ratioOfAssumed ?? 0) >= 1
                              ? 'var(--green)'
                              : (data.scrapCover.ratioOfAssumed ?? 0) >= 0.5
                                ? 'var(--amber, #b58900)'
                                : 'var(--red)',
                        }}
                      >
                        پوشش ضایعات: درآمد {formatRial(data.scrapCover.actualIncome)} ={' '}
                        {formatPercent(data.scrapCover.actualPct)} فروش (فرض{' '}
                        {formatPercent(data.scrapCover.assumedPct)})
                        {data.scrapCover.shortfall > 0 ? (
                          <>
                            {' '}
                            — کسری پوشش {formatRial(data.scrapCover.shortfall)}؛ غافلگیری‌ها
                            بی‌پوشش‌اند
                          </>
                        ) : (
                          <> — هزینه‌های پیش‌بینی‌نشده پوشش دارند ✓</>
                        )}
                      </div>
                    ) : null}
                    {/* Owner model: did the scrap income actually ABSORB the month's surprise costs (cost
                        inflation offer→dispatch + conversion under-absorption), with float left over? */}
                    {data.coverAssessment && data.coverAssessment.drawn > 0 ? (
                      <div
                        style={{
                          marginTop: 4,
                          color: data.coverAssessment.adequate ? 'var(--green)' : 'var(--red)',
                        }}
                      >
                        غافلگیری‌ها: فرسایش بها {formatRial(data.coverAssessment.costInflation)} +
                        کسری جذب {formatRial(data.coverAssessment.underAbsorption)} ={' '}
                        {formatRial(data.coverAssessment.drawn)} — پوشش با درآمد ضایعات{' '}
                        {formatRial(data.coverAssessment.cover)}:{' '}
                        {data.coverAssessment.adequate ? (
                          <b>شناور باقی‌مانده {formatRial(data.coverAssessment.headroom)} ✓</b>
                        ) : (
                          <b>کسری {formatRial(-data.coverAssessment.headroom)} ⚠ — به حاشیه خورد</b>
                        )}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {/* BIDX-P4 — the same NAMED figure the reconciliation worksheet carries, on the screen where
                    the month gets frozen. INFORMATIONAL ONLY, and that is an owner decision, not an
                    oversight: it must NOT block the close and must NOT be wired to the ack tick above.
                    Blocking here would habituate that tick and kill it for the real divergences it exists
                    to catch — and BIDX-P6 did NOT change that: the rials leaving the books is exactly why
                    the close must stay open, not a reason to gate it. Wording follows the worksheet's,
                    which P6 inverted from «داخل دفاتر» to «کنار گذاشته شده». */}
                {data && data.unpriced.count > 0 ? (
                  <div
                    style={{
                      marginTop: 12,
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid var(--amber, #b58900)',
                      lineHeight: 1.9,
                    }}
                  >
                    <div style={{ color: 'var(--amber, #b58900)', fontWeight: 600 }}>
                      قیمت تأییدنشده — در انتظار تأیید
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <b>{formatRial(data.unpriced.rials)}</b> ریال روی{' '}
                      <b>{faNum(data.unpriced.count)}</b> رسید خرید، با قیمتِ برآوردشده از سفارش
                      (قیمت پرداختی وارد نشده)
                      {data.unpriced.oldestDays > 0 ? (
                        <> — قدیمی‌ترین {faNum(data.unpriced.oldestDays)} روز</>
                      ) : null}
                      . <b>این عدد کلِ دفتر است، نه فقط این ماه</b> — شامل رسیدهایی از ماه‌های دیگر
                      هم می‌شود و با تغییر ماهِ انتخاب‌شده عوض نمی‌شود. این مبلغ از دفاتر کنار
                      گذاشته شده — نه سند حسابداری دارد، نه در «پرداختنی» و نه در ارزش‌گذاری موجودی
                      می‌آید (کالایش اما در انبار شمرده می‌شود) — پس این دو رقم تا این اندازه کمتر
                      از واقع‌اند. بستن ماه را متوقف نمی‌کند؛ ولی تا وقتی قیمت واقعی ثبت نشود،
                      «دستور ساخت»ی که مقدار موردنیازش از موجودیِ دارای قیمت تأییدشدهٔ آن ماده بیشتر
                      باشد تکمیل نمی‌شود (اگر مقدار موردنیاز داخل همان موجودیِ تأییدشده جا شود، مثل
                      همیشه تکمیل می‌شود).{' '}
                      {canPrice ? (
                        <>برای تأیید، در «خرید» ← «تاریخچهٔ خرید» «ثبت قیمت» را بزنید.</>
                      ) : (
                        <>
                          تأیید با «ثبت قیمت» در «خرید» ← «تاریخچهٔ خرید» انجام می‌شود؛ اگر دسترسی
                          خرید ندارید، از واحد خرید بخواهید آن را ثبت کند.
                        </>
                      )}
                    </div>
                  </div>
                ) : null}
                {!canEdit ? (
                  <p className="muted">فقط مدیر می‌تواند اعداد پایان ماه را وارد کند.</p>
                ) : data?.isLocked ? (
                  <div className="row" style={{ marginTop: 12, gap: 10, alignItems: 'center' }}>
                    <span className="muted">
                      🔒 ماه بسته و قفل است — گزارش سود این ماه تغییرناپذیر.
                    </span>
                    <Button variant="secondary" onClick={reopen} loading={busy}>
                      بازگشایی برای ویرایش
                    </Button>
                  </div>
                ) : (
                  <div style={{ marginTop: 12 }}>
                    {/* FLOW-4 (Q-6): WARN-and-ACKNOWLEDGE. The lock freezes this month's P&L; if the cached
                        reconciliation headline genuinely diverges, make the residual VISIBLE and require an
                        explicit ack before «بستن و قفل» — the server audits and enforces the same gate. */}
                    {data?.bookHealth?.diverged ? (
                      <div
                        style={{
                          marginBottom: 12,
                          padding: '10px 12px',
                          borderRadius: 8,
                          border: '1px solid var(--red)',
                          background: 'color-mix(in srgb, var(--red) 8%, transparent)',
                        }}
                      >
                        <div style={{ color: 'var(--red)', fontWeight: 600 }}>
                          ⚠ مغایرت دفترها پیش از قفل
                        </div>
                        <div style={{ marginTop: 4 }}>
                          کنترلِ ناتراز
                          {data.bookHealth.worstLabel ? (
                            <>
                              : <b>{data.bookHealth.worstLabel}</b>
                            </>
                          ) : null}
                          {data.bookHealth.worstResidual > 0 ? (
                            <>
                              {' '}
                              — باقیماندهٔ مغایرت <b>{formatRial(data.bookHealth.worstResidual)}</b>
                            </>
                          ) : null}
                          . بستن ماه، سود و زیان همین ماه را قفل و تغییرناپذیر می‌کند.
                        </div>
                        <label
                          className="row"
                          style={{ marginTop: 8, gap: 8, alignItems: 'center', cursor: 'pointer' }}
                        >
                          <input
                            type="checkbox"
                            checked={ackResidual}
                            onChange={(e) => setAckResidual(e.target.checked)}
                          />
                          <span>با آگاهی از این مغایرت، بستن و قفل ماه را تأیید می‌کنم.</span>
                        </label>
                      </div>
                    ) : null}
                    <div className="row">
                      <Button
                        onClick={save}
                        loading={busy}
                        disabled={busy || (!!data?.bookHealth?.diverged && !ackResidual)}
                      >
                        بستن و قفل ماه
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ),
          },
          {
            id: 'erosion',
            label: 'پارتو فرسایش',
            render: () =>
              data && data.byDealErosion.length ? (
                <Card title="پارتو فرسایش تورمی — کدام قراردادها حاشیه را خوردند؟">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>قرارداد</th>
                        <th>مشتری</th>
                        <th>تعداد</th>
                        <th>فرسایش (واقعی − انتظار)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.byDealErosion.map((d) => (
                        <tr key={d.dealId}>
                          <td className="num">{d.dealNo}</td>
                          <td>{d.customer}</td>
                          <td className="num">{d.qty}</td>
                          <td style={{ color: d.erosion > 0 ? 'var(--red)' : 'var(--green)' }}>
                            {formatRial(d.erosion)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              ) : (
                <Card>
                  <p className="muted" style={{ margin: 0 }}>
                    برای این ماه فرسایش تورمی‌ای ثبت نشده.
                  </p>
                </Card>
              ),
          },
          {
            id: 'yield',
            label: 'ضایعات',
            // Measured material-yield variance — read-only, D6-safe (never combined with COGS/cover).
            render: () => <YieldVarianceCard ym={ym} />,
          },
          {
            id: 'products',
            label: 'به تفکیک محصول',
            render: () =>
              products.length ? (
                <Card title="خروج انبار این ماه (به تفکیک محصول)">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>محصول</th>
                        <th>تعداد</th>
                        <th>درآمد</th>
                        <th>سود ناخالص</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map(([name, v]) => (
                        <tr key={name}>
                          <td>{name}</td>
                          <td>{v.qty}</td>
                          <td>{formatRial(v.revenue)}</td>
                          <td>{formatRial(v.gross)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Card>
              ) : (
                <Card>
                  <p className="muted" style={{ margin: 0 }}>
                    در این ماه خروج انباری ثبت نشده — تحویل‌ها را در قراردادهای قطعی ثبت کنید.
                  </p>
                </Card>
              ),
          },
        ]}
      />
    </>
  );
}
